import { IconButton, Menu, MenuButton, MenuItem, MenuList, useColorMode, useDisclosure } from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { ConfirmModal, StringEntryModal } from '@/components/modals';
import type League from '@/models/league';
import { updateLeague, deleteLeague as _deleteLeague } from '@/models/league';
import type { Firestore } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { FirebaseContext } from '@/session/FirebaseContext';
import { useContext } from 'react';
import { pageButtonProps } from '@/theme/sitePageTypography';

interface MenuAreaProps {
  isAdmin: boolean;
  league: League;
  leagueId: string;
  db: Firestore;
}

export function MenuArea({ isAdmin, league, leagueId, db }: MenuAreaProps) {
  const colorMode = useColorMode();
  const editNameDisclosure = useDisclosure();
  const deleteLeagueDisclosure = useDisclosure();
  const leaveLeagueDisclosure = useDisclosure();
  const navigate = useNavigate();
  const ctx = useContext(FirebaseContext);

  async function deleteLeague() {
    navigate('/../../profile', { replace: true });
    await _deleteLeague(db, leagueId);
  }

  async function leaveLeague() {
    httpsCallable(ctx.funcs, 'leaveLeague')({ leagueUID: leagueId });
    navigate('/../../profile', { replace: true });
  }

  return (
    <>
      <Menu placement='top' gutter={8}>
        <MenuButton
          as={IconButton}
          {...pageButtonProps}
          aria-label='League menu'
          icon={<HamburgerIcon />}
          variant='outline'
        />
        <MenuList fontSize='sm'>
          {isAdmin ? (
            <MenuItem onClick={editNameDisclosure.onOpen} fontSize='sm'>
              Edit league name
            </MenuItem>
          ) : null}
          {isAdmin ? (
            <>
              <MenuItem
                onClick={deleteLeagueDisclosure.onOpen}
                textColor={colorMode.colorMode === 'light' ? 'red.500' : 'red.300'}
                fontSize='sm'
              >
                Delete league
              </MenuItem>

              <ConfirmModal
                isOpen={deleteLeagueDisclosure.isOpen}
                title='Delete League'
                message='Are you sure that you would like to delete your league?'
                onClose={deleteLeagueDisclosure.onClose}
                isDestructive
                onConfirm={deleteLeague}
              />
            </>
          ) : (
            <>
              <MenuItem
                onClick={leaveLeagueDisclosure.onOpen}
                textColor={colorMode.colorMode === 'light' ? 'red.500' : 'red.300'}
                fontSize='sm'
              >
                Leave league
              </MenuItem>

              <ConfirmModal
                isOpen={leaveLeagueDisclosure.isOpen}
                title='Leave League'
                message='Are you sure that you would like to leave this league?'
                onClose={leaveLeagueDisclosure.onClose}
                isDestructive
                onConfirm={leaveLeague}
              />
            </>
          )}
        </MenuList>
      </Menu>

      <StringEntryModal
        isOpen={editNameDisclosure.isOpen}
        title='Edit Name'
        label='League Name'
        onClose={editNameDisclosure.onClose}
        onSubmit={(name: string) => updateLeague(db, leagueId, { name })}
        getInitialValue={() => league.name}
      />
    </>
  );
}
